import React, { useState } from "react";
import AdminLayout from "@/pages/admin/AdminLayout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const STATUSES = ["DRAFT", "NEEDS_REVIEW", "SEO_REVIEW", "LEGAL_REVIEW", "READY", "PUBLISHED", "ARCHIVED"];
const COLOR = { DRAFT: "#64748b", NEEDS_REVIEW: "#d97706", SEO_REVIEW: "#7c3aed", LEGAL_REVIEW: "#dc2626", READY: "#0891b2", PUBLISHED: "#16a34a", ARCHIVED: "#94a3b8" };

export default function GuidesAdmin() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const { data: guides = [], isLoading } = useQuery({ queryKey: ["admin-guides"], queryFn: () => api.get("/admin/guides").then((r) => r.data) });

  const openEdit = async (slug) => { const r = await api.get(`/admin/guides/${slug}`); setEditing(r.data); };

  const mut = useMutation({
    mutationFn: ({ slug, body }) => api.patch(`/admin/guides/${slug}`, body).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-guides"] }); toast.success("Guida aggiornata"); setEditing(null); },
    onError: () => toast.error("Errore"),
  });

  const save = () => mut.mutate({ slug: editing.slug, body: {
    title: editing.title, excerpt: editing.excerpt, body: editing.body, category: editing.category,
    status: editing.status, meta_title: editing.meta_title, meta_description: editing.meta_description,
  } });

  return (
    <AdminLayout>
      <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1" style={{ fontFamily: "Outfit" }}>Guide (CMS)</h1>
      <p className="text-slate-500 mb-6">Gestisci lo stato editoriale e i contenuti SEO delle guide.</p>

      {isLoading ? <p className="text-slate-500">Caricamento...</p> : (
        <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
          <table className="w-full text-sm" data-testid="guides-table">
            <thead className="bg-slate-50 text-slate-500 text-left"><tr><th className="px-4 py-3 font-semibold">Titolo</th><th className="px-4 py-3 font-semibold">Categoria</th><th className="px-4 py-3 font-semibold">Stato</th><th className="px-4 py-3"></th></tr></thead>
            <tbody>
              {guides.map((g) => (
                <tr key={g.slug} className="border-t border-slate-100 hover:bg-emerald-50/40">
                  <td className="px-4 py-3 font-semibold text-slate-900">{g.title}</td>
                  <td className="px-4 py-3 text-slate-600">{g.category}</td>
                  <td className="px-4 py-3"><span className="text-xs font-bold text-white px-2.5 py-1 rounded-full" style={{ backgroundColor: COLOR[g.status] || "#64748b" }}>{g.status}</span></td>
                  <td className="px-4 py-3 text-right"><Button size="sm" variant="outline" onClick={() => openEdit(g.slug)} data-testid={`edit-guide-${g.slug}`}>Modifica</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {editing && (
            <>
              <DialogHeader><DialogTitle>Modifica guida</DialogTitle><DialogDescription>Aggiorna contenuto, stato editoriale e metadati SEO della guida.</DialogDescription></DialogHeader>
              <div className="space-y-3">
                <div><label className="text-sm font-semibold text-slate-700">Titolo</label><Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="mt-1" data-testid="guide-title" /></div>
                <div><label className="text-sm font-semibold text-slate-700">Estratto</label><Textarea value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} rows={2} className="mt-1" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-sm font-semibold text-slate-700">Stato</label>
                    <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                      <SelectTrigger className="mt-1" data-testid="guide-status"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><label className="text-sm font-semibold text-slate-700">Categoria</label><Input value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="mt-1" /></div>
                </div>
                <div><label className="text-sm font-semibold text-slate-700">Meta description</label><Textarea value={editing.meta_description || ""} onChange={(e) => setEditing({ ...editing, meta_description: e.target.value })} rows={2} className="mt-1" /></div>
                <div><label className="text-sm font-semibold text-slate-700">Contenuto (HTML)</label><Textarea value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} rows={8} className="mt-1 font-mono text-xs" data-testid="guide-body" /></div>
                <Button onClick={save} disabled={mut.isPending} className="w-full h-11 font-bold text-white rounded-full" style={{ backgroundColor: "var(--sc-emerald)" }} data-testid="guide-save">Salva</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
